import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Icon,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import AppService from '../../../../Configurations/Appservice';
import { useDispatch } from 'react-redux';
import {
  showLoader,
  dismissLoader,
  showSuccessMessage,
} from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import DocumentTypeFormDialog from './DocumentTypeFormDialog';

/**
 * Document Types Management Page
 * Lists all document types with CRUD operations and expandable details
 * Uses mock data during UI-First phase
 */
const DocumentTypesPage = () => {
  const dispatch = useDispatch();
  const [documentTypes, setDocumentTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Load data from API
  useEffect(() => {
    loadCategories();
    loadDocumentTypes();
  }, []);

  const loadCategories = () => {
    AppService.get('api/organization-document-categories/list-details')
      .then((response) => {
        setCategories(response || []);
      })
      .catch((error) => {
        console.error('Failed to load categories', error);
      });
  };

  const loadDocumentTypes = () => {
    setLoading(true);
    AppService.get('api/organization-document-types')
      .then((response) => {
        setDocumentTypes(response || []);
      })
      .catch((error) => {
        console.error('Failed to load document types', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Filter document types
  useEffect(() => {
    let filtered = documentTypes;

    if (selectedCategory) {
      filtered = filtered.filter(t => t.categoryId === selectedCategory || t.categoryId?.toString() === selectedCategory?.toString());
    }

    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by category name
    filtered = [...filtered].sort((a, b) => {
      const catA = a.categoryName || '';
      const catB = b.categoryName || '';
      return catA.localeCompare(catB);
    });

    setFilteredTypes(filtered);
  }, [searchTerm, selectedCategory, documentTypes]);

  const handleAddClick = () => {
    setSelectedType(null);
    setDialogOpen(true);
  };

  const handleEditClick = (docType) => {
    setSelectedType(docType);
    setDialogOpen(true);
  };

  const handleDeleteClick = (docType) => {
    if (docType.isSystem) {
      alert('Cannot delete system document types.');
      return;
    }
    setSelectedType(docType);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setSelectedType(null);
  };

  const handleDelete = () => {
    dispatch(showLoader());
    AppService.delete(`api/organization-document-types/${selectedType.id}`)
      .then(() => {
        dispatch(showSuccessMessage('Document type deleted successfully'));
        loadDocumentTypes();
        setDeleteConfirmOpen(false);
        setSelectedType(null);
      })
      .catch((error) => {
        console.error('Failed to delete document type', error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedType(null);
  };

  const handleSave = async (typeData) => {
    dispatch(showLoader());
    const requestData = {
      code: typeData.code,
      name: typeData.name,
      nameAr: typeData.nameAr,
      categoryId: typeData.categoryId,
      hasExpiry: typeData.hasExpiry,
      isRenewable: typeData.isRenewable,
      documentNumberLabel: typeData.documentNumberLabel,
      documentNumberPattern: typeData.documentNumberPattern,
      issuingAuthority: typeData.issuingAuthority,
      isActive: typeData.isActive,
    };
    if (selectedType) {
      // Update existing
      AppService.put(`api/organization-document-types/${selectedType.id}`, requestData)
        .then(() => {
          dispatch(showSuccessMessage('Document type updated successfully'));
          loadDocumentTypes();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to update document type', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    } else {
      // Add new
      AppService.post('api/organization-document-types', requestData)
        .then(() => {
          dispatch(showSuccessMessage('Document type created successfully'));
          loadDocumentTypes();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to create document type', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId || c.id?.toString() === categoryId?.toString());
    return category?.name || 'Unknown';
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(c => c.id === categoryId || c.id?.toString() === categoryId?.toString());
    return category?.colour || category?.color || '#607D8B';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            Document Types
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure document types with custom fields, authorities, and validation rules
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Document Type
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: category.colour || category.color }} />
                  {category.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search document types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Authority</TableCell>
              <TableCell align="center">Expiry</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No document types found
                </TableCell>
              </TableRow>
            ) : (
              filteredTypes.map((docType) => (
                <TableRow key={docType.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon fontSize="small" color="action">{docType.icon || 'description'}</Icon>
                      <Typography variant="body2" fontWeight="medium">
                        {docType.code}
                      </Typography>
                      {docType.isSystem && (
                        <Chip label="System" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{docType.name}</Typography>
                      {docType.nameAr && (
                        <Typography variant="caption" color="text.secondary" dir="rtl">
                          {docType.nameAr}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryName(docType.categoryId)}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(docType.categoryId),
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{docType.issuingAuthority || '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={docType.hasExpiry ? 'Yes' : 'No'}
                      size="small"
                      color={docType.hasExpiry ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={docType.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={docType.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditClick(docType)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(docType)}
                        disabled={docType.isSystem}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: {filteredTypes.length} document types
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active: {filteredTypes.filter(t => t.isActive).length}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          System: {filteredTypes.filter(t => t.isSystem).length}
        </Typography>
      </Box>

      {/* Form Dialog */}
      <DocumentTypeFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        documentType={selectedType}
        categories={categories}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>Delete Document Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the document type "{selectedType?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentTypesPage;
